import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DfCatalogUiModule } from '@df/catalog-ui/df-catalog-ui.module';
import { Listing } from '@df/catalog-ui/listing/listing';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { IRelatedProductsEngineHandler, RelatedProductsService } from '@df/catalog-ui/related-products/related-products.service';
import { IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { Exponea } from '@df/module-exponea/exponea.types';
import { GtmTrackViewItemListDirective } from '@df/module-gtm/track/view-item-list/gtm-track-view-item-list.directive';
import { DfImageWrapperComponent } from '@df/module-image/df-image-wrapper.component';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, NICmsContentField } from '@icms/core/content';
import findIndex from 'lodash-es/findIndex';
import { Subject } from 'rxjs/internal/Subject';
import { CmsBlogPostComponent } from '../../../../../cms/blog/post/cms-blog-post.component';
import { IPioRelatedEngineOptions } from '../../../../../module/pio/related-products/pio-engine';
import { CustomCursorComponent } from '../../../../../ui/display/custom-cursor/custom-cursor.component';
import { CategoryComponent } from '../../../category/category.component';
import { ProductComponent } from '../../../product/product.component';
import { addExponeaRecommendationsVariant } from '../exponea-recommendations/exponea-recommendations.config';
import { IExponeaRecommendationsOptions } from '../exponea-recommendations/exponea-recommendations.interface';
import { LocalModelCarouselComponent } from '../local-model-carousel/local-model-carousel.component';
import { getFields } from '../local-model-carousel/local-model-carousel.config';
import { ILocalModelCarouselData } from '../local-model-carousel/local-model-carousel.interface';

const fields = getFields('product');
const variantIndex = findIndex(fields, i => i.key === 'variant');
const variant = <NICmsContentField.IChoiceField>fields[variantIndex];

variant.options = [{ key: 'pio', title: 'PIO similarity' }];
variant.default = 'pio';

const limitIndex = findIndex(fields, i => i.key === 'product_list');

fields.splice(limitIndex, 0, {
    key: 'initialStepsVisible',
    type: NICmsContentField.EFieldType.number,
    title: 'Initial Steps loaded',
    description: 'Steps showing up at page load',
    default: 1
});

fields.splice(limitIndex + 1, 0, {
    key: 'infScrollStep',
    type: NICmsContentField.EFieldType.number,
    title: 'Step Size',
    description: 'Number of items loaded per infinite scroll step.',
    default: 8
});

addExponeaRecommendationsVariant(fields);

interface IProductCarouselRelatedData extends ILocalModelCarouselData, IExponeaRecommendationsOptions {
    initialStepsVisible: number;
    infScrollStep: number;
}

export const PAGE_COMPONENT_RELATED_PRODUCTS_CAROUSEL_ID = 'related-products-carousel';

@Component({
    selector: 'related-products-carousel',
    templateUrl: './../local-model-carousel/local-model-carousel.html',
    styleUrl: './../local-model-carousel/local-model-carousel.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CategoryComponent,
        CmsBlogPostComponent,
        CommonModule,
        CustomCursorComponent,
        DfCatalogUiModule,
        DfImageDirective,
        DfImageWrapperComponent,
        DfUiCarouselModule,
        GtmTrackViewItemListDirective,
        GridModule,
        HrefComponent,
        ICmsClientModule,
        forwardRef(() => ProductComponent),
        RouterModule,
        SizeClassDirective,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<RelatedProductsCarouselComponent>({
    id: PAGE_COMPONENT_RELATED_PRODUCTS_CAROUSEL_ID,
    name: 'Related Products Carousel',
    fields
})
export class RelatedProductsCarouselComponent
    extends LocalModelCarouselComponent<Product<IProductDataV2>, IProductCarouselRelatedData>
    implements IRelatedProductsEngineHandler<Product<IProductDataV2>>, OnInit
{
    type = 'product';
    /**
     * Listing creation
     */
    readonly $listing = new Subject<ProductListing<Product<IProductDataV2>>>();
    override listing: ProductListing<Product<IProductDataV2>>;

    private relatedProductsService = inject(RelatedProductsService);

    /**
     * Product model currently viewed for printing context
     */
    get metaProduct(): Product<IProductDataV3> | undefined {
        const model = this.app.meta.model;
        if (model?.type === 'product') {
            return <Product<IProductDataV3>>model;
        }
        return undefined;
    }

    override set models(models: Product<IProductDataV2>[]) {
        if (this.listing) {
            (this.listing as Listing<Product<IProductDataV2>>).setFinalModels(models);
        }
    }

    override get models(): Product<IProductDataV2>[] {
        return (this.listing?.models as Product<IProductDataV2>[]) || [];
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(
            this.app.meta.subscribe(() => {
                this.setModels();
            })
        );
    }

    protected override setModels() {
        const sourceProduct = this.metaProduct;
        const ids = sourceProduct && [sourceProduct.id];

        if (ids?.length) {
            let engineOps;

            switch (this.component.variant) {
                case <string>'pio':
                    engineOps = <IPioRelatedEngineOptions>{
                        engine: 'similar'
                    };

                    break;
                case <string>Exponea.recommendationsEngineId:
                    engineOps = <Exponea.IRecommendationsEngineOptions>{
                        options: this.component.exponeaOptions
                    };

                    break;
            }

            const options = {
                engines: [{ id: this.component.variant, options: engineOps }],
                normalize: true
            };

            const state = JSON.stringify([ids, options]);
            const currentState = state;

            this.relatedProductsService.get(ids, this, options, (relatedIds: number[]) => {
                if (state === currentState) {
                    this.setListing(this.app.resources.product.getAllExisting(relatedIds) as Product<IProductDataV2>[]);
                }
            });
        } else {
            this.setListing([]);
        }
    }

    /**
     * Ger models request
     */
    protected override getLoadRequest(): IProductCarouselRelatedData {
        return this.component;
    }

    private setListing(models: Product<IProductDataV2>[]) {
        this.models = models;
        const data = this.getLoadRequest();
        const limit = data?.limit || 36;
        const infScrollStep = data?.infScrollStep || 8;
        const infScrollInit = Math.min(limit, infScrollStep);
        const initialStepsVisible = data?.initialStepsVisible || 1;

        if (this.listing) {
            this.listing.setSourceModels(models).applyFilters();
        } else {
            // we should change logic to use listing, for now its only used cause of IRelatedProductsEngineHandler
            this.listing = this.productListingFactory.create(
                {
                    type: 'product',
                    routableCheck: true,
                    initialStepsVisible: initialStepsVisible,
                    infScrollInit,
                    infScrollStep: infScrollStep
                },
                models
            );

            this.listingSubscription = this.listing.subscribe(() => {
                this.onModelsSet();
                this.detectChanges();
            });
        }

        this.listing.setLimit(infScrollInit);
        this.$listing.next(this.listing);
        this.onModelsSet();

        this.detectChanges();
    }
}
